<template>
  <!-- Need to stop touchmove event propagation, otherwise it is broken -->
  <div class="no-scrollbar flex space-x-2 overflow-x-auto" @touchmove.stop>
    <button
      v-for="(passenger, i) in passengers"
      :key="i"
      ref="seatMapPassengersRef"
      type="button"
      class="seat-map-passenger flex shrink-0 items-center space-x-2 rounded-2xl border p-3 duration-200"
      :class="getPassengerStateColor(passengersKeys[i])"
      @click="emit('onPassengerSelect', passengersKeys[i])"
    >
      <div
        class="rounded-2xl px-3 py-3"
        :class="
          checkIsActive(passengersKeys[i])
            ? 'bg-green-100'
            : 'bg-button-secondary-default'
        "
      >
        <!--eslint-disable-next-line-->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="16"
          fill="none"
        >
          <path
            fill="currentColor"
            d="M4.6 9.6a4 4 0 0 0-4 4A2.4 2.4 0 0 0 3 16h8a2.4 2.4 0 0 0 2.4-2.4 4 4 0 0 0-4-4H4.6Z"
          />
          <path fill="currentColor" d="M7 0a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
          <defs>
            <linearGradient
              id="a"
              x1="3.136"
              x2="10.864"
              y1="17.128"
              y2="9.408"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="currentColor" stop-opacity=".3" />
              <stop offset="1" stop-color="currentColor" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <p class="flex flex-col text-left">
        <span class="text-text-neutral-primary text-left text-sm">{{
          getPassengerName(passenger)
        }}</span>
        <span class="text-xs">
          {{ getPassengerSeatText(passengersKeys[i]) }}
        </span>
      </p>
      <button
        v-if="
          seatMapLib.hasPassengerSeat(passengersSeatState, passengersKeys[i])
        "
        type="button"
        @click.stop="emit('onPassengerSeatUnselect', passengersKeys[i])"
      >
        <!--eslint-disable-next-line-->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
        >
          <g
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
          >
            <path d="m6 6 12 12M18 6 6 18" />
          </g>
        </svg>
      </button>
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { prettyPrice } from '@aviata/text-fns';
  import { useI18n } from 'vue-i18n';
  import type {
    PassengersData,
    Passenger,
    PassengerKey,
  } from '@aviata/booking-data';

  import { seatMapLib } from '../lib';
  import type { PassengersState } from '../types';

  const props = defineProps<{
    passengersData: PassengersData;
    passengersKeys: PassengerKey[];
    activePassengerKey: PassengerKey;
    passengersSeatState: PassengersState;
  }>();

  const emit = defineEmits<{
    onPassengerSelect: [PassengerKey];
    onPassengerSeatUnselect: [PassengerKey];
  }>();

  const { t } = useI18n();
  const passengers = computed(() =>
    props.passengersKeys
      .map(key => props.passengersData[key])
      .filter(passengers => !!passengers),
  );
  const seatMapPassengersRef = ref<HTMLElement[]>();

  function getPassengerName(passenger: Passenger) {
    return `${passenger.lastName} ${passenger.firstName[0]}.`;
  }

  function checkIsActive(passengerKey: PassengerKey) {
    return props.activePassengerKey === passengerKey;
  }

  function getPassengerStateColor(passengerKey: PassengerKey) {
    if (checkIsActive(passengerKey)) {
      return '--active';
    }
    if (seatMapLib.hasPassengerSeat(props.passengersSeatState, passengerKey)) {
      return '--has-seat';
    }
  }

  function getPassengerSeatText(passengerKey: PassengerKey) {
    const seat = props.passengersSeatState[passengerKey];
    if (seat.seatKey) {
      return `${
        seat.seatPrice > 0
          ? `${seat.seatKey} — ${prettyPrice(String(seat.seatPrice))} ₸`
          : `${seat.seatKey} — ${t('general.price.free')}`
      }`;
    }
    return t('ancillaries.seatMap.seatNotSelected');
  }

  function scrollToActivePassenger() {
    seatMapPassengersRef.value
      ?.find(passenger => passenger.classList.contains('--active'))
      ?.scrollIntoView({
        block: 'nearest',
        inline: 'center',
        behavior: 'smooth',
      });
  }

  watch(
    computed(() => props.activePassengerKey),
    () => {
      nextTick(scrollToActivePassenger);
    },
  );
</script>

<style scoped>
  .seat-map-passenger {
    --seat-map-passegner-bg-color: theme('colors.background.neutral.onblock');
    --seat-map-passegner-border-color: theme('colors.transparent');
    --seat-map-passegner-text-color: theme('colors.situational.grey');
    background: var(--seat-map-passegner-bg-color);
    color: var(--seat-map-passegner-text-color);
    border-color: var(--seat-map-passegner-border-color);
  }
  .--active {
    --seat-map-passegner-bg-color: theme('colors.transparent');
    --seat-map-passegner-border-color: theme(
      'colors.background.positive.primary'
    );
    --seat-map-passegner-text-color: theme('colors.text.positive.primary');
  }

  .--has-seat {
    --seat-map-passegner-bg-color: theme('colors.background.neutral.onblock');
    --seat-map-passegner-border-color: theme('colors.transparent');
    --seat-map-passegner-text-color: theme('colors.text.neutral.primary');
  }
</style>
